import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import moment from 'moment';

import TooltipContentWrapper from '@common/components/tooltip_container/tooltip_content_wrapper';
import { addFlyout, FLYOUT_TYPES } from '@common/utils/flyout';
import DoubleInLineSkeleton from '../../skeletons/double_in_line_skeleton';

import './reservation_cell.scss';

const CLASS_NAME = 'reservation-cell';
const START_TIME_FORMAT = 'HH:mm:ss';
const START_TIME_FINAL_FORMAT = 'h:mma';
const START_DATE_FORMAT = 'YYYY-MM-DD';
const START_DATE_FINAL_FORMAT = 'MMM D';
const withoutTime = 'no time set';
const RESERVATION_TYPES = {
  B2B: 'b2b',
  CHECKIN: 'checkin',
  CHECKOUT: 'checkout',
  OCCUPIED: 'occupied',
};
const HIGHLIGHT_FILTERS = ['recent', 'checkin', 'checkout'];

const formatDate = date => date && moment(date, START_DATE_FORMAT).format(START_DATE_FINAL_FORMAT);
const formatTime = time => time && moment(time, START_TIME_FORMAT).format(START_TIME_FINAL_FORMAT);
const onReservationClick = reservationId => () => {
  addFlyout(FLYOUT_TYPES.RESERVATION_DETAILS, {
    reservationId,
  });
};
const formatDateWithTime = (date, time) => date && `${date}${time ? `, ${time}` : ''}`;
const getHighlightedReservations = (checkin, checkout, highlightValues) => {
  const values = _.without(highlightValues, 'recent');

  if (_.includes(highlightValues, 'recent')) {
    const checkinCreatedDate = moment(_.get(checkin, 'created_at'));
    const checkoutCreatedDate = moment(_.get(checkout, 'created_at'));

    if (checkinCreatedDate.isAfter(moment().subtract(7, 'days'))) {
      values.push('checkin');
    }
    if (checkoutCreatedDate.isAfter(moment().subtract(7, 'days'))) {
      values.push('checkout');
    }
  }

  return values;
};

const reservationBarSetup = (checkin, checkout, occupied, highlightValues) => {
  const type = {};

  if (!_.isEmpty(occupied)) {
    const checkinDate = formatDate(_.get(occupied, 'checkin_date'));
    const checkinTime = formatTime(_.get(occupied, 'checkin_time'));
    const checkoutDate = formatDate(_.get(occupied, 'checkout_date'));
    const checkoutTime = formatTime(_.get(occupied, 'checkout_time'));
    const checkinFormattedDate = formatDateWithTime(checkinDate, checkinTime);
    const checkoutFormattedDate = formatDateWithTime(checkoutDate, checkoutTime);
    const fullDate = `${checkinFormattedDate || withoutTime} - ${checkoutFormattedDate ||
      withoutTime}`;

    return {
      date: fullDate,
      occupied_guest_name: _.get(occupied, 'type_guest.name'),
      occupied_id: _.get(occupied, 'id'),
      title: 'Occupied',
      type: RESERVATION_TYPES.OCCUPIED,
    };
  }

  const checkinTime = _.get(checkin, 'checkin_time');
  const checkoutTime = _.get(checkout, 'checkout_time');

  if (checkin && checkout) {
    type.checkin_guest_name = _.get(checkin, 'type_guest.name');
    type.checkout_guest_name = _.get(checkout, 'type_guest.name');
    type.checkin_id = _.get(checkin, 'id');
    type.checkout_id = _.get(checkout, 'id');
    type.title = 'Back to back';
    type.type = RESERVATION_TYPES.B2B;

    const formattedCheckinTime = checkinTime ? formatTime(checkinTime) : withoutTime;
    const formattedCheckoutTime = checkoutTime ? formatTime(checkoutTime) : withoutTime;

    type.date = `${formattedCheckoutTime} - ${formattedCheckinTime}`;
  } else if (checkin && !checkout) {
    type.date = checkinTime ? formatTime(checkinTime) : withoutTime;
    type.checkin_guest_name = _.get(checkin, 'type_guest.name');
    type.checkin_id = _.get(checkin, 'id');
    type.title = 'Checkin';
    type.type = RESERVATION_TYPES.CHECKIN;
  } else if (!checkin && checkout) {
    type.date = checkoutTime ? formatTime(checkoutTime) : withoutTime;
    type.checkout_guest_name = _.get(checkout, 'type_guest.name');
    type.checkout_id = _.get(checkout, 'id');
    type.title = 'Checkout';
    type.type = RESERVATION_TYPES.CHECKOUT;
  }

  if (!_.isEmpty(highlightValues)) {
    _.set(type, 'highlighted', getHighlightedReservations(checkin, checkout, highlightValues));
  }

  return type;
};

const ReservationCell = (props, customProps) => {
  const checkin = _.get(props, 'checkin');
  const checkout = _.get(props, 'checkout');
  const occupied = _.get(props, 'occupied');

  const containerHeight = _.get(customProps, 'containerHeight', window.innerHeight);
  const containerWidth = _.get(customProps, 'containerWidth', window.outerWidth);
  const defaultCell = _.get(customProps, 'defaultCell');
  const withSkeletons = _.get(customProps, 'withSkeletons');
  const withTooltip = _.get(customProps, 'withTooltip');
  const reservationFilters = _.get(customProps, 'filters');
  const highlightValues = _.intersection(HIGHLIGHT_FILTERS, reservationFilters);
  const shouldHighlight = !_.isEmpty(highlightValues);

  if (withSkeletons && (_.isUndefined(checkin) || _.isUndefined(checkout))) {
    return <DoubleInLineSkeleton />;
  }

  const isEmpty =
    _.isEmpty(_.keys(checkin)) && _.isEmpty(_.keys(checkout)) && _.isEmpty(_.keys(occupied));

  if (isEmpty) {
    return <div className="default-cell--empty"> - </div>;
  }

  const type = reservationBarSetup(checkin, checkout, occupied, highlightValues);

  const renderReservationBar = () => (
    <div
      className={classnames(`${CLASS_NAME}__container`, {
        '-with-highlighting': shouldHighlight,
        '-without-separator': type.type === RESERVATION_TYPES.OCCUPIED,
      })}
    >
      {type.type === RESERVATION_TYPES.OCCUPIED && (
        <button
          className={`${CLASS_NAME}__occupied`}
          onClick={onReservationClick(type.occupied_id)}
          type="button"
        >
          <span>{type.occupied_guest_name}</span>
        </button>
      )}

      {(type.type === RESERVATION_TYPES.CHECKOUT || type.type === RESERVATION_TYPES.B2B) && (
        <button
          className={classnames(`${CLASS_NAME}__checkout`, {
            '-highlight': shouldHighlight && _.includes(type.highlighted, 'checkout'),
          })}
          onClick={onReservationClick(type.checkout_id)}
          type="button"
        >
          <span>{type.checkout_guest_name}</span>
        </button>
      )}

      {(type.type === RESERVATION_TYPES.CHECKIN || type.type === RESERVATION_TYPES.B2B) && (
        <button
          className={classnames(`${CLASS_NAME}__checkin`, {
            '-highlight': shouldHighlight && _.includes(type.highlighted, 'checkin'),
          })}
          onClick={onReservationClick(type.checkin_id)}
          type="button"
        >
          <span>{type.checkin_guest_name}</span>
        </button>
      )}
    </div>
  );

  const tooltipContent = type.date && (
    <div className={`${CLASS_NAME}__tooltip-time`}>{type.date}</div>
  );

  return (
    <TooltipContentWrapper
      className={classnames(`${CLASS_NAME}__wrapper`, {
        '-center': type.type === RESERVATION_TYPES.B2B || type.type === RESERVATION_TYPES.OCCUPIED,
        '-left': type.type === RESERVATION_TYPES.CHECKOUT,
        '-right': type.type === RESERVATION_TYPES.CHECKIN,
      })}
      containerHeight={containerHeight}
      containerWidth={containerWidth}
      content={renderReservationBar()}
      defaultCell={defaultCell}
      tooltipContent={tooltipContent}
      withArrow
      withTooltip={withTooltip}
    />
  );
};

ReservationCell.propTypes = {
  checkin: PropTypes.shape({}),
  checkout: PropTypes.shape({}),
  occupied: PropTypes.shape({}),
};

export default ReservationCell;
